import type { MouseEventHandler } from 'react'
import React, { useMemo } from 'react'
import { FormattedMessage } from 'react-intl'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import noop from 'lib/function/noop'
import { LUXURY_PLUS, LUXURY_PLUS_PATHS } from 'luxPlus/constants/base'
import { connect } from 'react-redux'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import FormatCurrency from 'components/Common/FormatCurrency'
import { getIsEnabledLuxPlusDiscountedInsurance, getIsEnabledLuxPlusDiscountedBookingProtection } from 'luxPlus/selectors/featureToggle'

interface MappedProps {
  subscription: App.ProductSubscription
  isEnabledLuxPlusDiscountedInsurance: boolean
  isEnabledLuxPlusDiscountedBookingProtection: boolean
}

interface Props extends MappedProps {
  onTsAndCsLinkClick?: MouseEventHandler<HTMLAnchorElement> | (() => void)
  type?: 'withPrice' | 'firstTimeDiscounted' | 'insuranceAndBookingProtection'
}

/**
 * LuxPlus subscription terms and conditions inside a `<BodyText>`.
 */
function LuxPlusSubscriptionTsAndCs(props: Props) {
  const {
    onTsAndCsLinkClick = noop,
    type,
    subscription,
    isEnabledLuxPlusDiscountedInsurance,
    isEnabledLuxPlusDiscountedBookingProtection,
  } = props

  const defaultTsAndCs = useMemo(() => <FormattedMessage
    id="70kok0ufL9FvOU4TID5j"
    defaultMessage="Your {programName} annual membership renews on your sign-up anniversary date. You can cancel your renewal anytime. <termsLink>T&Cs apply.</termsLink>"
    description="LuxPlus subscription default terms and conditions text"
    values={{
      programName: LUXURY_PLUS.PROGRAM_NAME,
      termsLink: (chunks) => <TextLink onClick={onTsAndCsLinkClick} weight="regular" to={`/${LUXURY_PLUS_PATHS.TERMS_AND_CONDITIONS_PAGE}`}>{chunks}</TextLink>,
    }}
  />, [onTsAndCsLinkClick])

  const copy = useMemo<React.ReactElement>(() => {
    switch (type) {
      case 'withPrice':
        return <>
          {subscription.error && defaultTsAndCs}
          {subscription.fetching && <LoadingIndicator />}
          {subscription.offers && <FormattedMessage
            id="QuPHZod5FHjaRycuungK"
            defaultMessage="Your {programName} {price} annual membership renews on your sign-up anniversary date. You can cancel your renewal anytime. {joinFee} one-off joining fee will be waived when you purchase this offer. <termsLink>T&Cs apply.</termsLink>"
            description="LuxPlus subscription terms with membership price and joining fee waiver details"
            values={{
              programName: LUXURY_PLUS.PROGRAM_NAME,
              price: <FormatCurrency value={subscription.offers[0].price} format="roundedDollar" />,
              joinFee: <FormatCurrency value={subscription.offers[0].joinFee} format="roundedDollar" />,
              termsLink: (chunks) => <TextLink onClick={onTsAndCsLinkClick} weight="regular" to={`/${LUXURY_PLUS_PATHS.TERMS_AND_CONDITIONS_PAGE}`}>{chunks}</TextLink>,
            }}
          />}
        </>
      case 'firstTimeDiscounted':
        return <>
          {subscription.error && defaultTsAndCs}
          {subscription.fetching && <LoadingIndicator />}
          {subscription.offers && <FormattedMessage
            id="Q4Mp_PskScNmgBTpsGvr"
            defaultMessage="*Discount applies to first year of membership. After that, {programName} renews at {renewalPrice} annually on your sign-up anniversary. You can cancel your renewal anytime. The {joinFee} one-off joining fee will be waived when you book this offer. <termsLink>T&Cs apply.</termsLink>"
            description="LuxPlus first-time discounted subscription terms with renewal price and joining fee waiver"
            values={{
              programName: LUXURY_PLUS.PROGRAM_NAME,
              renewalPrice: <FormatCurrency value={subscription.offers[0].price} />,
              joinFee: <FormatCurrency value={subscription.offers[0].joinFee} format="rounded" />,
              termsLink: (chunks) => <TextLink onClick={onTsAndCsLinkClick} weight="regular" to={`/${LUXURY_PLUS_PATHS.TERMS_AND_CONDITIONS_PAGE}`}>{chunks}</TextLink>,
            }}
          />}
        </>
      case 'insuranceAndBookingProtection':
        if (isEnabledLuxPlusDiscountedInsurance && isEnabledLuxPlusDiscountedBookingProtection) {
          return <FormattedMessage
            id="FFiD-_OjoZNM2Fd8uPWB"
            defaultMessage="^Members receive {discountPercentage}% off Travel and Cancellation Protection product(s) (Product) up to a maximum discount of {maxDiscount}. The Product may not be available to all members and eligibility and coverage restrictions apply. Members must read and consider the relevant PDS to ensure whether and how the Product applies to them. The relevant terms and PDS of the Product will apply to any purchase. The Product benefit is not exchangeable for cash or credit if not able to be utilised for any reason. <termsLink>T&Cs apply.</termsLink>"
            description="LuxPlus discount terms for both Travel and Cancellation Protection products"
            values={{
              discountPercentage: LUXURY_PLUS.INSURANCE_DISCOUNT_PERCENTAGE,
              maxDiscount: <FormatCurrency value={LUXURY_PLUS.MAX_INSURANCE_DISCOUNT_THRESHOLD} />,
              termsLink: (chunks) => <TextLink onClick={onTsAndCsLinkClick} weight="regular" to={`/${LUXURY_PLUS_PATHS.TERMS_AND_CONDITIONS_PAGE}`}>{chunks}</TextLink>,
            }}
          />
        }
        if (isEnabledLuxPlusDiscountedInsurance) {
          return <FormattedMessage
            id="9S3kyiXmIIw5s1NobDrq"
            defaultMessage="^Members receive {discountPercentage}% off Travel Protection product(s) (Product) up to a maximum discount of {maxDiscount}. The Product may not be available to all members and eligibility and coverage restrictions apply. Members must read and consider the relevant PDS to ensure whether and how the Product applies to them. The relevant terms and PDS of the Product will apply to any purchase. The Product benefit is not exchangeable for cash or credit if not able to be utilised for any reason. <termsLink>T&Cs apply.</termsLink>"
            description="LuxPlus discount terms for Travel Protection products only"
            values={{
              discountPercentage: LUXURY_PLUS.INSURANCE_DISCOUNT_PERCENTAGE,
              maxDiscount: <FormatCurrency value={LUXURY_PLUS.MAX_INSURANCE_DISCOUNT_THRESHOLD} />,
              termsLink: (chunks) => <TextLink onClick={onTsAndCsLinkClick} weight="regular" to={`/${LUXURY_PLUS_PATHS.TERMS_AND_CONDITIONS_PAGE}`}>{chunks}</TextLink>,
            }}
          />
        }
        if (isEnabledLuxPlusDiscountedBookingProtection) {
          return <FormattedMessage
            id="ZCSjf5KhD3Yl4Xq1edAs"
            defaultMessage="^Members receive {discountPercentage}% off Cancellation Protection product(s) (Product) up to a maximum discount of {maxDiscount}. The Product may not be available to all members and eligibility and coverage restrictions apply. Members must read and consider the relevant PDS to ensure whether and how the Product applies to them. The relevant terms and PDS of the Product will apply to any purchase. The Product benefit is not exchangeable for cash or credit if not able to be utilised for any reason. <termsLink>T&Cs apply.</termsLink>"
            description="LuxPlus discount terms for Cancellation Protection products only"
            values={{
              discountPercentage: LUXURY_PLUS.INSURANCE_DISCOUNT_PERCENTAGE,
              maxDiscount: <FormatCurrency value={LUXURY_PLUS.MAX_INSURANCE_DISCOUNT_THRESHOLD} />,
              termsLink: (chunks) => <TextLink onClick={onTsAndCsLinkClick} weight="regular" to={`/${LUXURY_PLUS_PATHS.TERMS_AND_CONDITIONS_PAGE}`}>{chunks}</TextLink>,
            }}
          />
        }
        return defaultTsAndCs
      default:
        return defaultTsAndCs
    }
  }, [defaultTsAndCs, isEnabledLuxPlusDiscountedBookingProtection, isEnabledLuxPlusDiscountedInsurance, onTsAndCsLinkClick, subscription.error, subscription.fetching, subscription.offers, type])

  return <BodyText variant="small" colour="neutral-one" data-testid="luxury-plus-subscription-ts-and-cs">
    {copy}
  </BodyText>
}

const mapStateToProps = (state: App.State): MappedProps => ({
  subscription: state.luxPlus.products.subscription,
  isEnabledLuxPlusDiscountedInsurance: getIsEnabledLuxPlusDiscountedInsurance(state),
  isEnabledLuxPlusDiscountedBookingProtection: getIsEnabledLuxPlusDiscountedBookingProtection(state),
})

export default connect(mapStateToProps)(LuxPlusSubscriptionTsAndCs)
